import * as React from 'react';
import { ArticleHeading1, ModalFullscreen } from '@cian/ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import { BigHeading } from '../../components/BigHeading';
import { DealSalePromoModalLayout } from '../../components/DealSalePromoModalLayout';
import { DealStepCardDesktop } from '../../components/DealStepCardDesktop';
import { DealStepCardHandheld } from '../../components/DealStepCardHandheld';
import { DealStepsList } from '../../components/DealStepsList';
import { DealStepsModalActionsDesktop } from '../../components/DealStepsModalActionsDesktop';
import { DealStepsModalActionsMobile } from '../../components/DealStepsModalActionsMobile';
import { NBSP } from '../../constants/symbols';
import { IWithDeviceTypeProps, withDeviceType } from '../../utils/withDeviceType';
import { DealFeaturesModalHeader } from '../DealFeaturesModalContainer/components';
import { ApplicationStepModalContent } from './components/ApplicationStepModalContent';
import { DocumentSigningStepModalContent } from './components/DocumentSigningStepModalContent';
import { RosreestrRegistrationStepModalContent } from './components/RosreestrRegistrationStepModalContent';
import { dealStepCards, EDealStepModalTypes } from './content';
import { trackGetConsultationClick } from './tracking';
import { useDealStepsModalNavigation } from './utils/useDealStepsModalNavigation';
import { setCommonDealDetailsModalId } from '../../actions';
import { TCommonDealDetailKeys } from '../../types/promoInfo';
import { trackCommonDealDetailsModalOpen } from '../../utils/analytics';
import { selectCommonDealDetails, selectIsAccreditiveActive } from '../../selectors/promoInfo';
import { ConsultationRequestButtonContainer } from '../ConsultationRequestButtonContainer';
import { useApplicationContext } from '../../utils/applicationContext';

/**
 * Секция "Лёгкий путь к сделке за 3 шага".
 */
const DealStepsSectionContainer: React.FC<React.PropsWithChildren<IWithDeviceTypeProps>> = props => {
  const dispatch = useDispatch();
  const { config } = useApplicationContext();
  const isSbrEnabled = config.get<boolean>('e2e-deal-sale.Sbr.Enabled') || false;

  const commonDealDetails = useSelector(selectCommonDealDetails);
  const isAccreditiveActive = useSelector(selectIsAccreditiveActive);
  const [modalProps, openDealStepModal, modalStepNavigationProps, modalTopRef] = useDealStepsModalNavigation();

  const HeadingComponent = props.isPhone ? ArticleHeading1 : BigHeading;
  const DealStepCard = props.isDesktop ? DealStepCardDesktop : DealStepCardHandheld;
  const ActionsComponent = props.isDesktop ? DealStepsModalActionsDesktop : DealStepsModalActionsMobile;

  const onCardClick = (modalTitle: string) => (subModalId: TCommonDealDetailKeys) => {
    dispatch(setCommonDealDetailsModalId(subModalId));
    trackCommonDealDetailsModalOpen(modalTitle, commonDealDetails[subModalId].title);
  };

  const modalContent = (() => {
    if (!modalProps.open) {
      return null;
    }

    return {
      [EDealStepModalTypes.Application]: (
        <ApplicationStepModalContent
          isAccreditiveActive={isAccreditiveActive}
          title="Заявка и проверка"
          isSbrEnabled={isSbrEnabled}
          onCardClick={onCardClick('Заявка и проверка')}
        />
      ),
      [EDealStepModalTypes.DocumentSigning]: (
        <DocumentSigningStepModalContent
          isAccreditiveActive={isAccreditiveActive}
          title="Подписание документов"
          isSbrEnabled={isSbrEnabled}
          onCardClick={onCardClick('Подписание документов')}
        />
      ),
      [EDealStepModalTypes.RosreestrRegistration]: (
        <RosreestrRegistrationStepModalContent
          isAccreditiveActive={isAccreditiveActive}
          title={`Регистрация в${NBSP}Росреестре`}
          isSbrEnabled={isSbrEnabled}
          onCardClick={onCardClick(`Регистрация в${NBSP}Росреестре`)}
        />
      ),
    }[modalProps.modalType];
  })();

  return (
    <>
      <HeadingComponent as="h2">
        Лёгкий путь к сделке за{NBSP}3{NBSP}шага
      </HeadingComponent>
      <DealStepsList>
        {dealStepCards.map((card, i) => (
          <DealStepCard {...card} key={i} onLearnMoreClick={() => openDealStepModal({ modalType: card.modal })} />
        ))}
      </DealStepsList>
      <ModalFullscreen onClose={modalProps.onClose} open={modalProps.open}>
        <div ref={modalTopRef} aria-hidden />
        <DealFeaturesModalHeader onClose={modalProps.onClose} />
        <DealSalePromoModalLayout>
          {modalContent}
          {modalProps.open && (
            <ActionsComponent
              {...modalStepNavigationProps}
              action={
                <ConsultationRequestButtonContainer
                  size="M"
                  theme={props.isDesktop ? 'fill_secondary' : 'fill_primary'}
                  onGetConsultationClick={() => trackGetConsultationClick(modalProps.modalType)}
                >
                  Получить консультацию
                </ConsultationRequestButtonContainer>
              }
            />
          )}
        </DealSalePromoModalLayout>
      </ModalFullscreen>
    </>
  );
};

const DealStepsSectionContainerWithDeviceType = withDeviceType(DealStepsSectionContainer);
export { DealStepsSectionContainerWithDeviceType as DealStepsSectionContainer };
